import Pusher from 'pusher-js';
import { pusherConfig } from '../config/pusher';
import type { RealtimeConnectionState } from './types';

let pusherClient: Pusher | null = null;

const normalizeConnectionState = (state: string): RealtimeConnectionState => {
  switch (state) {
    case 'initialized':
      return 'idle';
    case 'connecting':
      return 'connecting';
    case 'connected':
      return 'connected';
    case 'unavailable':
      return 'unavailable';
    case 'failed':
      return 'failed';
    case 'disconnected':
      return 'disconnected';
    default:
      return 'idle';
  }
};

const buildPusherClient = (): Pusher => {
  const client = new Pusher(pusherConfig.key, {
    cluster: pusherConfig.cluster,
    forceTLS: pusherConfig.forceTLS,
    ...(pusherConfig.authEndpoint ? { authEndpoint: pusherConfig.authEndpoint } : {}),
  });

  return client;
};

export const getPusherClient = (): Pusher | null => {
  if (!pusherConfig.enabled) {
    return null;
  }

  if (!pusherClient) {
    pusherClient = buildPusherClient();
  }

  return pusherClient;
};

export const disconnectPusherClient = (): void => {
  if (!pusherClient) {
    return;
  }

  pusherClient.disconnect();
  pusherClient = null;
};

export const bindPusherConnectionState = (
  onStateChange: (nextState: RealtimeConnectionState) => void
): (() => void) | null => {
  const client = getPusherClient();
  if (!client) {
    return null;
  }

  const handler = (changeState: { previous: string; current: string }) => {
    onStateChange(normalizeConnectionState(changeState.current));
  };

  client.connection.bind('state_change', handler);

  return () => {
    client.connection.unbind('state_change', handler);
  };
};

export const getInitialPusherConnectionState = (): RealtimeConnectionState => {
  const client = getPusherClient();
  if (!client) {
    return 'idle';
  }

  return normalizeConnectionState(client.connection.state);
};
